import React, { useEffect, useState } from "react";
import {
  HashRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import * as ChannelService from "@channel.io/channel-web-sdk-loader";
import Home from "./components/Home";
import Login from "./components/Login";
import About from "./components/About";
import PolicyPage from "./components/Policy/PolicyPage";
import HamburgerMenu from "./components/HamburgerMenu";
import GlobalStyle from "./styles/GlobalStyle";
import Nav from "./components/Nav";
import { UserInfo } from "./types/user";

// 채널톡 플러그인 키 — 빌드 env(REACT_APP_*)로 주입 가능, 없으면 운영 키 fallback.
const CHANNEL_TALK_PLUGIN_KEY =
  process.env.REACT_APP_CHANNEL_TALK_PLUGIN_KEY ||
  "68feaca1-0f6d-46ba-a315-589b0168cc8b";

const AppContent: React.FC = () => {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [samName, setSamName] = useState<string>("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  // Nav를 숨길 경로 설정
  const hideNavPaths = ["/home"];

  // 정책 페이지(이용약관·개인정보처리방침 등)는 가입 화면의 iframe·팝업으로 단독 노출된다.
  const isPolicyPage = location.pathname.startsWith("/policy");

  // Nav를 보여줄지 여부 결정
  const shouldShowNav =
    !hideNavPaths.includes(location.pathname) && !isPolicyPage;

  // 채널톡 부팅 — 모던 npm 로더(@channel.io/channel-web-sdk-loader).
  // 정책 페이지에서는 로드·부팅 자체를 건너뛰어 가입 화면 안에 채널톡이 뜨지 않게 한다.
  useEffect(() => {
    if (isPolicyPage) return;
    ChannelService.loadScript();
    ChannelService.boot({ pluginKey: CHANNEL_TALK_PLUGIN_KEY });
    // 정책 경로로 이동하거나 언마운트 시 정리 (메인→정책 SPA 전환 대비).
    return () => ChannelService.shutdown();
  }, [isPolicyPage]);

  useEffect(() => {
    // 쿠키에서 ACCESS_TOKEN 확인
    const getCookie = (name: string) => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop()?.split(";").shift();
    };

    const accessToken = getCookie("ACCESS_TOKEN");

    if (accessToken) {
      // API 호출
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "https://api-sch.smilebogun.org";
      fetch(`${apiBaseUrl}/v1/checkTokenSelf`, {
        method: "POST",
        headers: {
          Authorization: accessToken,
        },
      })
        .then((response) => {
          if (!response.ok) throw new Error("Auth check failed");
          return response.json();
        })
        .then((data) => {
          setUserInfo(data);
        })
        .catch(() => {
          // Error handling without console log in production
        });

      const sam = getCookie("SAM_NAME") ?? "";
      setSamName(sam);
    }
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <div className="app-container" style={{ paddingTop: "52px" }}>
      {shouldShowNav && (
        <Nav userInfo={userInfo} samName={samName} toggleMenu={toggleMenu} />
      )}

      <HamburgerMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        userInfo={userInfo}
        samName={samName}
      />

      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/home" element={<Home />} />
        <Route path="/policy/:id" element={<PolicyPage />} />
        <Route
          path="/*"
          element={
            location.pathname.startsWith("/sv") ? null : (
              <div className="content-container">
                <Routes>
                  <Route path="/about" element={<About />} />
                  <Route path="/login" element={<Login />} />
                </Routes>
              </div>
            )
          }
        />
      </Routes>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <GlobalStyle />
      <AppContent />
    </Router>
  );
};

export default App;
